import moment from "moment";

const toBooleanToggle = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }

  return value === 1 || value === true;
};

const toNumericToggle = (value) => (toBooleanToggle(value) ? 1 : 0);

const serializeQliAreas = (qliAreas = {}) => {
  const rawValues = Array.isArray(qliAreas.value) ? qliAreas.value : [];

  return {
    toggle: toBooleanToggle(qliAreas.toggle),
    value: rawValues.map((item) => ({
      ...item,
      label: item && item.display_name ? item.display_name : "",
      value: item && item.id !== undefined ? item.id : "",
      isFixed: true
    }))
  };
};

const serializeAreas = (areas = {}) => {
  const rawValues = Array.isArray(areas.values) ? areas.values : [];

  return {
    toggle: toBooleanToggle(areas.toggle),
    values: rawValues.map((item, index) => {
      if (item && typeof item === "object" && item.label && item.value) {
        return item;
      }

      const areaText = `${item || ""}`;
      const stableKey = `${areaText}::${index}`;
      return {
        label: areaText,
        value: stableKey,
        swmAreaKey: stableKey
      };
    })
  };
};

const serializeDocumentProjectDetails = (data = {}) => {
  return {
    ...data,
    issue_date: data.issue_date ? moment.utc(data.issue_date, "YYYY-MM-DD") : null,
    qli_areas: serializeQliAreas(data.qli_areas),
    areas: serializeAreas(data.areas)
  };
};

const serializeDocumentAuthorization = (data = {}) => {
  return {
    ...data,
    approvers: Array.isArray(data.approvers) ? data.approvers : [],
    toggle: toBooleanToggle(data.toggle),
    notes: data && data.notes !== undefined ? data.notes : "",
    noteToReviewers: data && data.noteToReviewers !== undefined ? data.noteToReviewers : null
  };
};

const serializeHighRiskActivities = (data = []) => {
  const categories = Array.isArray(data) ? data : [];

  return categories.map((category) => ({
    ...category,
    activities: Array.isArray(category && category.activities)
      ? category.activities.map((activity) => ({
        ...activity,
        selected: toBooleanToggle(activity && activity.selected)
      }))
      : []
  }));
};

const serializeMethodItemType = (data = {}) => {
  const methodItems = Array.isArray(data.method_items)
    ? data.method_items.map((item) => ({
      ...item,
      isNew: toBooleanToggle(item && (item.isNew || item.is_new)),
      selected: toBooleanToggle(item && item.selected)
    }))
    : [];

  const selected = methodItems
    .filter((item) => item.selected)
    .map((item) => ({
      ...item,
      label: item.method_item,
      value: item.id
    }));

  const enabled = {};
  methodItems.forEach((item) => {
    enabled[`item_${item.id}`] = toBooleanToggle(item.selected);
  });

  return {
    ...data,
    toggle: toBooleanToggle(data.toggle),
    method_items: methodItems,
    selected,
    enabled
  };
};

const serializeSwmsContentManagement = (data = {}) => {
  return {
    ...data,
    toggle: toBooleanToggle(data.toggle),
    description: data && data.description !== undefined ? data.description : ""
  };
};

const toDateString = (value) => {
  if (!value) {
    return null;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const serializeQliAreasForSubmit = (qliAreas = {}) => {
  const rawValues = Array.isArray(qliAreas.value) ? qliAreas.value : [];

  return {
    toggle: toNumericToggle(qliAreas.toggle),
    value: rawValues.map((item) => ({
      id: item && item.id !== undefined ? item.id : null,
      name: item && item.name ? item.name : "",
      display_name: item && item.display_name ? item.display_name : ""
    }))
  };
};

const serializeAreasForSubmit = (areas = {}) => {
  const rawValues = Array.isArray(areas.values) ? areas.values : [];

  return {
    toggle: toNumericToggle(areas.toggle),
    values: rawValues.map((item) => {
      if (item && typeof item === "object") {
        return item.label || item.value;
      }

      return item;
    })
  };
};

const serializeAttachmentsForSubmit = (attachments = []) => {
  const rawAttachments = Array.isArray(attachments) ? attachments : [];

  return rawAttachments
    .map((file) => {
      if (!file || !file.file_name || !file.file_name.file_name) {
        return null;
      }

      return {
        note: file.note,
        file_name: {
          file_name: file.file_name.file_name,
          visible_name: file.file_name.visible_name,
          type: file.file_name.type
        }
      };
    })
    .filter(Boolean);
};

const serializeDocumentProjectDetailsForSubmit = (data = {}) => {
  return {
    ...data,
    areas: serializeAreasForSubmit(data.areas),
    qli_areas: serializeQliAreasForSubmit(data.qli_areas),
    issue_date: toDateString(data.issue_date),
    attachments: serializeAttachmentsForSubmit(data.attachments)
  };
};

const serializeDocumentAuthorizationForSubmit = (data = {}) => {
  const approvers = Array.isArray(data.approvers) ? data.approvers : [];

  return {
    ...data,
    approvers,
    toggle: toNumericToggle(data.toggle),
    notes: data && data.notes !== undefined ? data.notes : "",
    noteToReviewers: data && data.noteToReviewers !== undefined ? data.noteToReviewers : null
  };
};

const serializeHighRiskActivitiesForSubmit = (data = []) => {
  const categories = Array.isArray(data) ? data : [];

  return categories.map((category) => ({
    ...category,
    activities: Array.isArray(category && category.activities)
      ? category.activities.map((activity) => ({
        ...activity,
        selected: toNumericToggle(activity && activity.selected)
      }))
      : []
  }));
};

const serializeMethodItemTypeForSubmit = (data = {}) => {
  const items = Array.isArray(data.method_items) ? data.method_items : [];
  const enabled = data && data.enabled ? data.enabled : {};

  return {
    toggle: toNumericToggle(data.toggle),
    method_items: items
      .filter((item) => item && item.method_item)
      .map((item) => ({
        ...item,
        is_new: toNumericToggle(item && item.isNew),
        selected: toNumericToggle(enabled[`item_${item.id}`])
      }))
  };
};

const serializeSwmsContentManagementForSubmit = (data = {}) => {
  return {
    ...data,
    toggle: toNumericToggle(data.toggle),
    description: data && data.description !== undefined ? data.description : ""
  };
};

const serializeSectionDataForSubmit = (sectionKey, data) => {
  if (sectionKey === "document_project_details") {
    return serializeDocumentProjectDetailsForSubmit(data);
  }

  if (sectionKey === "document_authorization") {
    return serializeDocumentAuthorizationForSubmit(data);
  }

  if (sectionKey === "high_risk_activities") {
    return serializeHighRiskActivitiesForSubmit(data);
  }

  if (sectionKey.startsWith("method_item_type_")) {
    return serializeMethodItemTypeForSubmit(data);
  }

  if (sectionKey.startsWith("swms_content_management_")) {
    return serializeSwmsContentManagementForSubmit(data);
  }

  return data;
};

export const serializeInitialValuesV3 = (swmsData = {}) => {
  const sections = swmsData && swmsData.sections && typeof swmsData.sections === "object"
    ? swmsData.sections
    : {};

  const serializedSections = {
    ...sections
  };

  if (serializedSections.document_project_details) {
    serializedSections.document_project_details = {
      ...serializedSections.document_project_details,
      data: serializeDocumentProjectDetails(
        serializedSections.document_project_details.data
      )
    };
  }

  if (serializedSections.document_authorization) {
    serializedSections.document_authorization = {
      ...serializedSections.document_authorization,
      data: serializeDocumentAuthorization(
        serializedSections.document_authorization.data
      )
    };
  }

  if (serializedSections.high_risk_activities) {
    serializedSections.high_risk_activities = {
      ...serializedSections.high_risk_activities,
      data: serializeHighRiskActivities(
        serializedSections.high_risk_activities.data
      )
    };
  }

  Object.keys(serializedSections).forEach((sectionKey) => {
    if (sectionKey.startsWith("method_item_type_")) {
      serializedSections[sectionKey] = {
        ...serializedSections[sectionKey],
        data: serializeMethodItemType(serializedSections[sectionKey].data)
      };
    }
  });

  Object.keys(serializedSections).forEach((sectionKey) => {
    if (sectionKey.startsWith("swms_content_management_")) {
      serializedSections[sectionKey] = {
        ...serializedSections[sectionKey],
        data: serializeSwmsContentManagement(serializedSections[sectionKey].data)
      };
    }
  });

  return {
    sections: serializedSections
  };
};

export const serializeSubmitValuesV3 = (formValues = {}, currentSwmsData = {}) => {
  const existingSections = currentSwmsData && currentSwmsData.sections && typeof currentSwmsData.sections === "object"
    ? currentSwmsData.sections
    : {};
  const formSections = formValues && formValues.sections && typeof formValues.sections === "object"
    ? formValues.sections
    : {};

  const sectionKeys = Array.from(new Set([
    ...Object.keys(existingSections),
    ...Object.keys(formSections)
  ]));

  const serializedSections = {};

  sectionKeys.forEach((sectionKey) => {
    const existingSection = existingSections[sectionKey] || {};
    const formSection = formSections[sectionKey] || {};
    const resolvedData = formSection.data !== undefined
      ? formSection.data
      : existingSection.data;

    serializedSections[sectionKey] = {
      ...existingSection,
      ...formSection,
      data: serializeSectionDataForSubmit(sectionKey, resolvedData)
    };
  });

  return {
    sections: serializedSections
  };
};

export const serializeSubmitPayloadV3 = (formValues = {}, currentSwmsData = {}) => {
  return {
    ...currentSwmsData,
    ...serializeSubmitValuesV3(formValues, currentSwmsData)
  };
};
